/**
 * The conversation header — V6-05 §1, §3, §5, §7.
 *
 * Everything that is true of the room rather than of a message: who it is with,
 * the participant sheet, mute (surfaced here per §7 rather than hidden in a
 * menu nobody found), and the destructive actions each type allows.
 *
 * Which actions appear is decided by the conversation's *type* and the
 * viewer's admin flag, both of which the server already sends — the client
 * does not re-derive policy, it renders what it was told.
 */

import { useState } from 'react';
import {
  Archive,
  ArrowLeft,
  AtSign,
  Bell,
  BellOff,
  Check,
  LogOut,
  Megaphone,
  MoreHorizontal,
  Pin,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserProfilePopover } from '@/components/shared/UserProfilePopover';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import {
  useAddMembers,
  useChatPeople,
  useClearConversation,
  useDeleteConversation,
  useGroupMembers,
  useLeaveConversation,
  usePromoteMember,
  useRemoveMember,
  useRenameConversation,
  usePins,
  useSetArchived,
  useSetNotifyLevel,
  useSetPostingMode,
  type ConversationView,
} from './api';

function titleOf(conversation: ConversationView): string {
  if (conversation.type === 'dm') return conversation.participants[0]?.name ?? 'Conversation';
  return conversation.name ?? 'Group';
}

/** The roster sheet — §5, each row opening the same profile popover. */
function MembersSheet({
  conversation,
  onClose,
  onLeft,
}: {
  conversation: ConversationView;
  onClose: () => void;
  onLeft: () => void;
}) {
  const { user } = useAuth();
  const { data: members = [] } = useGroupMembers(conversation._id);
  const [search, setSearch] = useState('');
  const { data: people = [] } = useChatPeople(search);
  const add = useAddMembers();
  const remove = useRemoveMember();
  const promote = usePromoteMember();
  const leave = useLeaveConversation();

  /**
   * V8-02 §4 — who may add and remove people here.
   *
   * This read `type === 'group'`, which was complete until V7-04 added
   * channels: a channel has admins and mutable membership on the server, and
   * this line hid the controls from them. Worse, the "only admins can add
   * people" explanation below was gated the same way — so a channel admin
   * opened the sheet, found a list of names, no way to add anybody and no
   * reason given. That is the exact shape of the V7-02 report, reintroduced for
   * the new type and never noticed because V7-04's tests never opened this
   * sheet for a channel.
   *
   * Asked of the policy rather than the type, so a fifth type cannot repeat it.
   */
  const managesMembership =
    conversation.type === 'group' || conversation.type === 'channel';
  const canManage = conversation.isAdmin && managesMembership;
  const adminNames = members.filter((member) => member.isAdmin).map((member) => member.name);

  async function run(action: () => Promise<unknown>, done: string): Promise<void> {
    try {
      await action();
      toast.success(done);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Participants"
      data-testid="members-sheet"
      className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="vt-surface vt-elevated flex max-h-[70vh] w-[340px] flex-col gap-2 overflow-y-auto p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[13px] font-medium">
          {members.length} {members.length === 1 ? 'person' : 'people'}
        </h3>

        <ul className="flex flex-col gap-1">
          {members.map((member) => (
            <li
              key={member._id}
              className="flex items-center gap-2 text-[13px]"
              data-testid="member-row"
              data-name={member.name}
            >
              <UserProfilePopover userId={member._id} name={member.name}>
                <span className="inline-flex items-center gap-1.5">
                  <UserAvatar
                    id={member._id}
                    name={member.name}
                    src={member.avatarUrl ?? undefined}
                    size={24}
                  />
                  <span className="truncate">{member.name}</span>
                </span>
              </UserProfilePopover>
              {member.isAdmin && <Badge tone="accent">admin</Badge>}

              {canManage && member._id !== user?._id && (
                <span className="ml-auto flex gap-1">
                  {!member.isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="member-promote"
                      onClick={() =>
                        void run(
                          () => promote.mutateAsync({ id: conversation._id, userId: member._id }),
                          `${member.name} is now an admin`,
                        )
                      }
                    >
                      Make admin
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${member.name}`}
                    data-testid="member-remove"
                    onClick={() =>
                      void run(
                        () => remove.mutateAsync({ id: conversation._id, userId: member._id }),
                        `${member.name} removed`,
                      )
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>

        {/*
          V7-02 §1 — the reported "cannot add a member" was this branch going
          quiet. A non-admin opened the sheet, found a list of names and no way
          to add anybody, and reasonably called it broken. The API was fine; the
          absence explained nothing. §1: "the UI must say 'Only group admins can
          add people', never render a dead control."
        */}
        {/**
         * V9-02 §1 — every type says something. No dead controls, and no
         * silence either.
         *
         * The company room used to render neither a control nor a word,
         * because both were gated on `managesMembership`. Somebody opening it
         * saw a list of names and no explanation for why nothing could be done
         * with them — the same complaint V7-02 answered for groups and V8-02
         * for channels, still unanswered for the third type.
         */}
        {conversation.type === 'company' && (
          <p className="text-[12px] vt-muted" data-testid="member-add-automatic">
            Everyone in the workspace is in this room automatically. People join when they are
            invited and leave when their account is closed, so there is nothing to add or remove
            here.
          </p>
        )}

        {conversation.type === 'dm' && (
          <p className="text-[12px] vt-muted" data-testid="member-add-denied">
            A direct message is between the two of you. Start a group or a channel to bring somebody
            else in.
          </p>
        )}

        {managesMembership && !canManage && (
          <p className="text-[12px] vt-muted" data-testid="member-add-denied">
            {adminNames.length > 0 ? (
              <>
                Only {conversation.type === 'channel' ? 'channel' : 'group'} admins can add people.
                Ask {adminNames.join(' or ')} to add someone.
              </>
            ) : (
              /**
               * V9-02 §1 — the case that produced the fourth report.
               *
               * A conversation migrated from V6-05 can have **no**
               * administrators at all, and "ask an admin" is useless advice
               * when there is not one. The nightly shape audit reports these
               * and the repair migration seeds one; until it runs, this at
               * least says something true.
               */
              <>
                This {conversation.type === 'channel' ? 'channel' : 'group'} has no administrators,
                so nobody can add or remove people. A workspace admin can repair it — this is a
                known fault in conversations created before administrators existed.
              </>
            )}
          </p>
        )}

        {canManage && (
          <>
            <Input
              placeholder="Add someone…"
              aria-label="Add someone to the group"
              data-testid="member-add-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search.trim() && (
              <div data-testid="member-add-results">
                {people
                  .filter((person) => !members.some((member) => member._id === person._id))
                  .map((person) => (
                    <button
                      key={person._id}
                      type="button"
                      data-testid="member-add-person"
                      data-name={person.name}
                      className="flex w-full items-center gap-2 px-1 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)]"
                      onClick={() =>
                        void run(async () => {
                          await add.mutateAsync({
                            id: conversation._id,
                            userIds: [person._id],
                          });
                          setSearch('');
                        }, `${person.name} added`)
                      }
                    >
                      <UserAvatar
                        id={person._id}
                        name={person.name}
                        src={person.avatarUrl}
                        size={22}
                      />
                      {person.name}
                    </button>
                  ))}
              </div>
            )}
          </>
        )}

        {/*
          V7-02 §3 — Leave was only ever in the header's "…" menu, behind an
          icon with no visible label. It worked; nobody could find it, which for
          a non-admin wondering how to get out of a group is the same thing.
          The participants sheet is where a person is already thinking about
          membership, so the way out belongs here too.
        */}
        {/**
         * V9-02 §1 — "members leave", for channels as well as groups.
         *
         * This was `type === 'group'`. A channel is leavable on the server
         * (`policy.mayLeave`) and always has been, but the only way out was the
         * actions menu in the header — which §2.3 calls hunting. The company
         * room stays out: nobody leaves it, and its line above says why.
         */}
        {managesMembership && (
          <Button
            variant="ghost"
            size="sm"
            className="justify-start text-[var(--danger)]"
            data-testid="members-leave"
            onClick={() =>
              void run(async () => {
                await leave.mutateAsync(conversation._id);
                onLeft();
              }, conversation.type === 'channel' ? 'You left the channel' : 'You left the group')
            }
          >
            <LogOut className="size-3.5" />
            Leave this {conversation.type === 'channel' ? 'channel' : 'group'}
          </Button>
        )}

        <Button variant="secondary" size="sm" data-testid="members-close" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/** §3 — typing the name is the confirmation, checked again on the server. */
function DeleteGroupDialog({
  conversation,
  onClose,
  onDeleted,
}: {
  conversation: ConversationView;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const remove = useDeleteConversation();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete group"
      data-testid="delete-group-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="vt-surface vt-elevated flex w-[320px] flex-col gap-2 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[13px] font-medium">Delete “{conversation.name}” for everyone?</h3>
        <p className="text-[12px] vt-muted">
          Every message and file goes with it, for every member. Type the group’s name to confirm.
        </p>
        <Input
          data-testid="delete-group-confirm"
          aria-label="Type the group name to confirm"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
        <div className="flex gap-1.5">
          <Button
            variant="danger"
            size="sm"
            data-testid="delete-group-submit"
            disabled={typed.trim() !== (conversation.name ?? '').trim() || remove.isPending}
            onClick={async () => {
              try {
                await remove.mutateAsync({ id: conversation._id, confirmName: typed.trim() });
                toast.success('Group deleted');
                onDeleted();
              } catch (err) {
                toast.error(err instanceof ApiError ? err.message : 'Could not delete the group');
              }
            }}
          >
            Delete for everyone
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ConversationHeader({
  conversation,
  onBack,
  onClosed,
  onJumpTo,
  onSearch,
}: {
  conversation: ConversationView;
  onBack: () => void;
  onClosed: () => void;
  /** V7-04 §5 — scroll to a pinned message. "Jump to context" is the feature. */
  onJumpTo?: (messageId: string) => void;
  /** V7-04 §6 — open search already narrowed to this conversation. */
  onSearch?: () => void;
}) {
  const [sheet, setSheet] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(conversation.name ?? '');
  const [deleting, setDeleting] = useState(false);

  const rename = useRenameConversation();
  const leave = useLeaveConversation();
  const clear = useClearConversation();
  /* ── V7-04 §1, §5, §8 ─────────────────────────────────── */
  const notify = useSetNotifyLevel();
  const archive = useSetArchived();
  const postingMode = useSetPostingMode();
  const pins = usePins(conversation._id);
  /**
   * §8 — the level as it will actually behave, not as it is stored.
   *
   * The server sends the *effective* level on the row, so an untouched channel
   * reads `mentions` here. Falling back to the old boolean keeps a cached row
   * from an older client rendering something wrong.
   */
  const level = conversation.notifyLevel ?? (conversation.muted ? 'mute' : 'all');

  const isDm = conversation.type === 'dm';
  const isCompany = conversation.type === 'company';
  const other = conversation.participants[0];

  async function run(action: () => Promise<unknown>, done: string): Promise<void> {
    try {
      await action();
      toast.success(done);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That did not work');
    }
  }

  return (
    <header
      className="flex items-center gap-2 border-b border-[var(--border)] p-2"
      data-testid="chat-header"
      data-type={conversation.type}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Back to conversations"
        data-testid="chat-back"
        className="md:hidden"
        onClick={onBack}
      >
        <ArrowLeft />
      </Button>

      {/* §5 — the header name opens the same popover a name in the thread does. */}
      {isDm && other ? (
        <UserProfilePopover userId={other._id} name={other.name}>
          <span className="inline-flex items-center gap-2">
            <UserAvatar id={other._id} name={other.name} size={24} />
            <span className="text-[13px] font-medium" data-testid="chat-thread-title">
              {other.name}
            </span>
          </span>
        </UserProfilePopover>
      ) : renaming ? (
        <Input
          className="h-7 max-w-[220px]"
          aria-label="Group name"
          data-testid="rename-input"
          autoFocus
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setRenaming(false);
            if (event.key === 'Enter') {
              setRenaming(false);
              void run(
                () => rename.mutateAsync({ id: conversation._id, name: draftName.trim() }),
                'Renamed',
              );
            }
          }}
          onBlur={() => setRenaming(false)}
        />
      ) : (
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          data-testid="chat-thread-title"
          onClick={() => setSheet(true)}
        >
          <span aria-hidden>{conversation.emoji ?? (isCompany ? '🏢' : '👥')}</span>
          <span className="text-[13px] font-medium">{titleOf(conversation)}</span>
          <span className="text-[11px] vt-muted" data-testid="chat-participant-count">
            {conversation.participantCount}
          </span>
        </button>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* V7-04 §6 — search inside this conversation, from its header. */}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Search this conversation"
          data-testid="chat-search-conversation"
          onClick={onSearch}
        >
          <Search />
        </Button>

        {/* V7-04 §5 — the room's pinned messages, if it has any. */}
        {(pins.data?.length ?? 0) > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Pinned messages"
                data-testid="chat-pins"
                data-pin-count={pins.data!.length}
              >
                <Pin />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-w-xs">
              {pins.data!.map((pinned) => (
                <DropdownMenuItem
                  key={pinned._id}
                  data-testid="chat-pinned-message"
                  onSelect={() => onJumpTo?.(pinned._id)}
                >
                  <span className="truncate">{pinned.body || 'Attachment'}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/**
         * V7-04 §8 — three levels, not a switch.
         *
         * The old mute toggle is still here for a DM, where "all or nothing" is
         * the whole question. A channel needs the middle setting, and it is the
         * one it starts on.
         */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Notification level"
              data-testid="chat-notify-level"
              data-level={level}
            >
              {level === 'mute' ? <BellOff /> : level === 'mentions' ? <AtSign /> : <Bell />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(
              [
                ['all', 'All messages'],
                ['mentions', 'Mentions only'],
                ['mute', 'Nothing'],
              ] as const
            ).map(([value, label]) => (
              <DropdownMenuItem
                key={value}
                data-testid={`chat-notify-${value}`}
                data-selected={level === value ? 'true' : 'false'}
                onSelect={() =>
                  void run(
                    () => notify.mutateAsync({ id: conversation._id, level: value }),
                    `Notifications: ${label.toLowerCase()}`,
                  )
                }
              >
                {label}
                {level === value && <Check className="ml-auto size-3.5" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {!isDm && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Participants"
            data-testid="chat-participants"
            onClick={() => setSheet(true)}
          >
            <Users />
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Conversation actions" data-testid="chat-actions">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {conversation.isAdmin && !isDm && (
              <DropdownMenuItem
                data-testid="chat-rename"
                onSelect={() => {
                  setDraftName(conversation.name ?? '');
                  setRenaming(true);
                }}
              >
                Rename
              </DropdownMenuItem>
            )}

            {/* §3 — a DM is cleared for you alone; the peer keeps theirs. */}
            {isDm && (
              <DropdownMenuItem
                destructive
                data-testid="chat-clear"
                onSelect={() =>
                  void run(async () => {
                    await clear.mutateAsync(conversation._id);
                    onClosed();
                  }, 'Chat cleared for you')
                }
              >
                <Trash2 className="size-3.5" />
                Delete chat
              </DropdownMenuItem>
            )}

            {/* V7-04 §1 — a channel's two admin switches. Archiving is
                reversible and read-only; announcements mode is "everyone
                reads, admins post". */}
            {conversation.type === 'channel' && conversation.isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="chat-posting-mode"
                  onSelect={() =>
                    void run(
                      () =>
                        postingMode.mutateAsync({
                          id: conversation._id,
                          mode: conversation.postingMode === 'admins' ? 'everyone' : 'admins',
                        }),
                      conversation.postingMode === 'admins'
                        ? 'Everyone can post again'
                        : 'Announcements mode on — only admins can post',
                    )
                  }
                >
                  <Megaphone className="size-3.5" />
                  {conversation.postingMode === 'admins'
                    ? 'Let everyone post'
                    : 'Announcements only'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="chat-archive"
                  onSelect={() =>
                    void run(
                      () =>
                        archive.mutateAsync({
                          id: conversation._id,
                          archived: !conversation.isArchived,
                        }),
                      conversation.isArchived ? 'Channel restored' : 'Channel archived',
                    )
                  }
                >
                  <Archive className="size-3.5" />
                  {conversation.isArchived ? 'Restore channel' : 'Archive channel'}
                </DropdownMenuItem>
              </>
            )}

            {(conversation.type === 'group' || conversation.type === 'channel') && (
              <>
                <DropdownMenuItem
                  data-testid="chat-leave"
                  onSelect={() =>
                    void run(async () => {
                      await leave.mutateAsync(conversation._id);
                      onClosed();
                    }, 'You left the group')
                  }
                >
                  <LogOut className="size-3.5" />
                  {conversation.type === 'channel' ? 'Leave channel' : 'Leave group'}
                </DropdownMenuItem>
                {conversation.isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      destructive
                      data-testid="chat-delete-group"
                      onSelect={() => setDeleting(true)}
                    >
                      <Trash2 className="size-3.5" />
                      Delete group for everyone
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}

            {isCompany && (
              // §2 — nobody leaves or deletes it; mute is the escape hatch, and
              // saying so is better than an absent menu people hunt through.
              <DropdownMenuItem disabled data-testid="chat-company-note">
                Everyone is in this room — mute it instead
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {sheet && (
        <MembersSheet
          conversation={conversation}
          onClose={() => setSheet(false)}
          onLeft={() => {
            setSheet(false);
            onClosed();
          }}
        />
      )}
      {deleting && (
        <DeleteGroupDialog
          conversation={conversation}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            onClosed();
          }}
        />
      )}
    </header>
  );
}
